import { LowerCasePipe, TitleCasePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  LOCALE_ID,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Store, select as selectState } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import type {
  Client,
  OmniSearchRequestByCompany,
  OmniSearchRequestByProvider,
  OmniSearchRequestByTenantId,
  TENANTSINTERFACE,
} from '@fitness-force/models';
import { ClientService, OmniSearchService } from '@fitness-force/api';
import { ElasticProviderName } from '@fitness-force/enum';
import { COMPANY_INFO, TENANTS_INFO } from '@fitness-force/state';
import { HighlightSearch } from '@fitness-force/pipes/common-pipes';
import { ShortNamePipe } from '@fitness-force/pipes/short-name';
import { AvatarModule } from 'primeng/avatar';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { Subject, Subscription, debounceTime, distinctUntilChanged } from 'rxjs';
import {
  MINI_SEARCH_V2_PROVIDERS,
  type MiniSearchV2StatLabel,
  type MiniSearchV2StatSegment,
  type MiniSearchV2TenantSearchResults,
  type MiniSearchV2TenantBucket,
} from './mini-search-v2.models';

@Component({
  selector: 'mini-search-v2',
  standalone: true,
  imports: [
    TitleCasePipe,
    LowerCasePipe,
    FormsModule,
    TranslateModule,
    DialogModule,
    InputTextModule,
    ButtonModule,
    AvatarModule,
    ProgressSpinnerModule,
    ShortNamePipe,
    HighlightSearch,
  ],
  templateUrl: './mini-search-v2.component.html',
  styleUrls: ['./mini-search-v2.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MiniSearchV2Component {
  private readonly omniSearchService = inject(OmniSearchService);
  private readonly clientService = inject(ClientService);
  private readonly store = inject(Store);
  private readonly translate = inject(TranslateService);
  private readonly locale = inject(LOCALE_ID);
  private readonly destroyRef = inject(DestroyRef);

  readonly isReadonly = input(false);
  readonly isDisabled = input(false);
  readonly clientId = input(0);
  readonly showSelected = input(true);
  readonly showCreateClient = input(true);
  readonly dialogTitle = input('');
  readonly dialogSubtitle = input('');

  readonly clients = output<Client>();
  readonly removedClient = output<void>();
  readonly createProspect = output<void>();

  readonly dialogOpen = signal(false);
  readonly searchInput = signal('');
  readonly loading = signal(false);
  /** When true, use company omnisearch; when false, tenant omnisearch. */
  readonly allCentresMode = signal(false);
  readonly segments = signal<MiniSearchV2StatSegment[]>([]);
  readonly allCentresTotal = signal(0);
  readonly thisCentreTotal = signal(0);
  readonly companyClients = signal<Client[]>([]);
  readonly companyPage = signal(1);
  readonly companyHasMore = signal(false);
  readonly selectedReadonlyClient = signal<Client | null>(null);
  readonly activeFilter = signal<'all' | MiniSearchV2StatLabel>('all');

  readonly thisCentreButtonLabel = signal('');

  private tenantId = 0;
  private allTenants: TENANTSINTERFACE['accessed_tenant'] = [];
  private loadClientByIdSub: Subscription | null = null;
  /** Cancels prior tenant/company omnisearch HTTP when a new query or mode runs. */
  private activeOmnisearchSub: Subscription | null = null;

  /** Invalidates in-flight omnisearch HTTP callbacks after close, clear, or new query. */
  private searchEpoch = 0;

  private readonly querySubject = new Subject<string>();

  readonly currentQuery = computed(() => this.searchInput().trim());

  readonly hasLocalResults = computed(() => this.segments().some((s) => s.total > 0));

  statLabel(label: MiniSearchV2StatLabel): string {
    const keys: Record<MiniSearchV2StatLabel, string> = {
      PROSPECT: 'CLIENT_OMNISEARCH.CLIENT.PROSPECT',
      ACTIVE: 'CLIENT_OMNISEARCH.CLIENT.ACTIVE',
      EX_CLIENT: 'CLIENT_OMNISEARCH.CLIENT.EX_CLIENT',
      BLOCKED: 'CLIENT_OMNISEARCH.CLIENT.BLOCKED',
    };
    return this.translate.instant(keys[label]);
  }

  private refreshThisCentreButtonLabel(tenant: TENANTSINTERFACE): void {
    const name =
      tenant.selected_tenant.name_primary_language ||
      tenant.selected_tenant.name_secondary_language ||
      '';
    const label = name
      ? this.translate.instant('Shared.SEARCH.MINI-SEARCH.THIS_CENTRE_WITH_NAME', { name })
      : this.translate.instant('Shared.SEARCH.MINI-SEARCH.THIS_CENTRE');
    this.thisCentreButtonLabel.set(label);
  }

  constructor() {
    this.store
      .pipe(selectState(COMPANY_INFO), takeUntilDestroyed(this.destroyRef))
      .subscribe((c) => {
        const lang = c?.selectedLang?.LANG_CODE;
        if (lang) {
          this.translate.setFallbackLang(lang);
          this.translate.use(lang);
        }
      });

    this.store
      .pipe(selectState(TENANTS_INFO), takeUntilDestroyed(this.destroyRef))
      .subscribe((tenant: TENANTSINTERFACE) => {
        this.allTenants = tenant.accessed_tenant ?? [];
        if (tenant.selected_tenant.id > 0) {
          this.tenantId = tenant.selected_tenant.id;
          this.refreshThisCentreButtonLabel(tenant);
        }
      });

    this.querySubject
      .pipe(
        debounceTime(400),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((q) => {
        void this.runDebouncedSearch(q);
      });

    effect(() => {
      const id = this.clientId();
      if (id > 0) {
        this.loadClientById(id);
      } else {
        this.loadClientByIdSub?.unsubscribe();
        this.loadClientByIdSub = null;
        this.selectedReadonlyClient.set(null);
      }
    });

    this.destroyRef.onDestroy(() => {
      this.loadClientByIdSub?.unsubscribe();
      this.activeOmnisearchSub?.unsubscribe();
      this.querySubject.complete();
    });
  }

  onDialogVisibleChange(visible: boolean): void {
    this.dialogOpen.set(visible);
    if (!visible) {
      this.loading.set(false);
      this.invalidateOmnisearchEpoch();
    }
  }

  openDialog(): void {
    if (this.isReadonly() || this.isDisabled()) {
      return;
    }
    this.dialogOpen.set(true);
    this.activeFilter.set('all');
    const q = this.currentQuery();
    if (q.length >= 3) {
      void this.runDebouncedSearch(q);
    }
  }

  closeDialog(): void {
    this.invalidateOmnisearchEpoch();
    this.dialogOpen.set(false);
    this.loading.set(false);
  }

  onSearchFieldInput(value: string): void {
    this.searchInput.set(value);
    this.querySubject.next(value.trim());
  }

  clearSearchInput(): void {
    this.invalidateOmnisearchEpoch();
    this.searchInput.set('');
    this.segments.set([]);
    this.companyClients.set([]);
    this.allCentresTotal.set(0);
    this.thisCentreTotal.set(0);
    this.companyHasMore.set(false);
    this.companyPage.set(1);
    this.querySubject.next('');
  }

  setCentreMode(allCentres: boolean): void {
    this.allCentresMode.set(allCentres);
    const q = this.currentQuery();
    if (q.length >= 3) {
      void this.runDebouncedSearch(q);
    } else {
      this.invalidateOmnisearchEpoch();
      this.segments.set([]);
      this.companyClients.set([]);
    }
  }

  setActiveFilter(filter: 'all' | MiniSearchV2StatLabel): void {
    this.activeFilter.set(filter);
  }

  emitCreateProspect(): void {
    this.createProspect.emit();
  }

  pickClient(client: Client): void {
    if (!client?.clt_id) {
      return;
    }
    this.selectedReadonlyClient.set(client);
    this.clients.emit(client);
    this.closeDialog();
    this.clearSearchInput();
  }

  clearReadonlyClient(): void {
    this.selectedReadonlyClient.set(null);
    this.removedClient.emit();
  }

  clientStatus(client: Client | null): string {
    if (client && (client.clt_status || (client as { client_status?: string }).client_status)) {
      return this.clientService.getClientStatusHtml(
        String(client.clt_status ?? (client as { client_status?: string }).client_status),
      );
    }
    return '';
  }

  getTenant(client: Client): { name_primary_language?: string } | null {
    if (client.clt_home_club_tenant_id) {
      const t = this.allTenants.find((x) => x.id === client.clt_home_club_tenant_id);
      return t ?? null;
    }
    if (client.clt_createdin_tenant_id) {
      const t = this.allTenants.find((x) => x.id === client.clt_createdin_tenant_id);
      return t ?? null;
    }
    return null;
  }

  client2LetterName(name: string): string {
    if (!name) {
      return '';
    }
    const nameArray = name.split(' ');
    const out: string[] = [];
    for (let i = 0; i < nameArray.length && out.length < 2; i++) {
      if (nameArray[i]) {
        out.push(nameArray[i]);
      }
    }
    return out.join(' ');
  }

  private invalidateOmnisearchEpoch(): void {
    this.searchEpoch++;
  }

  private beginOmnisearchEpoch(): number {
    return ++this.searchEpoch;
  }

  private isOmnisearchStale(epoch: number): boolean {
    return epoch !== this.searchEpoch;
  }

  private safeTenantBucket(
    b: MiniSearchV2TenantBucket | undefined | null,
  ): MiniSearchV2TenantBucket {
    if (b && Array.isArray(b.items) && typeof b.total_items === 'number') {
      return { total_items: b.total_items, items: b.items };
    }
    return { total_items: 0, items: [] };
  }

  visibleSegments(): MiniSearchV2StatSegment[] {
    const all = this.segments();
    const f = this.activeFilter();
    if (f === 'all') {
      return all;
    }
    return all.filter((s) => s.label === f);
  }

  allCentreCountBadge(): number {
    return this.allCentresTotal() || this.sumSegmentTotals();
  }

  thisCentreCountBadge(): number {
    return this.thisCentreTotal() || this.sumSegmentTotals();
  }

  private sumSegmentTotals(): number {
    return this.segments().reduce((acc, s) => acc + s.total, 0);
  }

  async loadMoreCompany(): Promise<void> {
    const q = this.currentQuery();
    if (q.length < 3 || !this.allCentresMode()) {
      return;
    }
    const epochAtLoadMore = this.searchEpoch;
    this.companyPage.update((p) => p + 1);
    await this.searchCompany(q, false, epochAtLoadMore);
  }

  expandSegment(seg: MiniSearchV2StatSegment): void {
    if (seg.fullyLoaded || seg.items.length >= seg.total || !this.tenantId) {
      return;
    }
    const epochAtExpand = this.searchEpoch;
    const request: OmniSearchRequestByProvider = {
      query: this.currentQuery(),
      must_not_include_client_ids: seg.items.map((c) => c.clt_id),
      pagination: { page_number: 1, page_size: seg.total },
      language: this.locale,
    };
    this.omniSearchService
      .SearchByProvider(this.tenantId, seg.provider, request)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: { items?: Client[] }) => {
          if (this.isOmnisearchStale(epochAtExpand)) {
            return;
          }
          const extra = response?.items ?? [];
          if (extra.length === 0) {
            return;
          }
          this.segments.update((list) =>
            list.map((s) => {
              if (s.label !== seg.label) {
                return s;
              }
              const merged = [...s.items, ...extra];
              return {
                ...s,
                items: merged,
                fullyLoaded: merged.length >= s.total,
              };
            }),
          );
        },
        error: () => {
          // Non-fatal: user can retry "show all"
        },
      });
  }

  private async runDebouncedSearch(trimmed: string): Promise<void> {
    if (!this.dialogOpen()) {
      return;
    }
    if (trimmed.length < 3) {
      this.invalidateOmnisearchEpoch();
      this.segments.set([]);
      this.companyClients.set([]);
      this.allCentresTotal.set(0);
      this.thisCentreTotal.set(0);
      this.companyHasMore.set(false);
      this.companyPage.set(1);
      return;
    }
    const epoch = this.beginOmnisearchEpoch();
    this.companyPage.set(1);
    if (this.allCentresMode()) {
      await this.searchCompany(trimmed, true, epoch);
    } else {
      await this.searchTenant(trimmed, epoch);
    }
  }

  private searchTenant(query: string, epoch: number): Promise<void> {
    if (!this.tenantId) {
      this.segments.set([]);
      this.loading.set(false);
      return Promise.resolve();
    }
    this.loading.set(true);
    const request: OmniSearchRequestByTenantId = {
      language: this.locale,
      query,
      top_n_results: 5,
    };
    return new Promise((resolve) => {
      this.activeOmnisearchSub?.unsubscribe();
      this.activeOmnisearchSub = this.omniSearchService
        .SearchByTenantId(String(this.tenantId), request)
        .subscribe({
          next: (raw: unknown) => {
            if (this.isOmnisearchStale(epoch)) {
              this.loading.set(false);
              resolve();
              return;
            }
            this.processTenantResponse(raw);
            this.loading.set(false);
            resolve();
          },
          error: () => {
            if (this.isOmnisearchStale(epoch)) {
              this.loading.set(false);
              resolve();
              return;
            }
            this.segments.set([]);
            this.thisCentreTotal.set(0);
            this.loading.set(false);
            resolve();
          },
        });
    });
  }

  private processTenantResponse(raw: unknown): void {
    const res = raw as {
      search_results?: MiniSearchV2TenantSearchResults;
      searchResults?: MiniSearchV2TenantSearchResults;
    };
    const sr = res?.search_results ?? res?.searchResults;
    if (!sr?.tenant_level_clients?.length) {
      this.segments.set([]);
      this.thisCentreTotal.set(0);
      this.allCentresTotal.set(sr?.total_items ?? 0);
      return;
    }
    const rawLevel = sr.tenant_level_clients[0];
    if (!rawLevel) {
      this.segments.set([]);
      this.thisCentreTotal.set(0);
      this.allCentresTotal.set(sr.total_items ?? 0);
      return;
    }
    const prospects = this.safeTenantBucket(rawLevel.prospects_and_trials);
    const active = this.safeTenantBucket(rawLevel.active_clients_and_non_members);
    const exClients = this.safeTenantBucket(rawLevel.ex_clients_and_non_members);
    const blocked = this.safeTenantBucket(rawLevel.blocked_clients);

    const next: MiniSearchV2StatSegment[] = [
      {
        label: 'ACTIVE',
        provider: MINI_SEARCH_V2_PROVIDERS.ACTIVE,
        total: active.total_items,
        items: [...active.items],
        fullyLoaded: false,
      },
      {
        label: 'EX_CLIENT',
        provider: MINI_SEARCH_V2_PROVIDERS.EX_CLIENT,
        total: exClients.total_items,
        items: [...exClients.items],
        fullyLoaded: false,
      },
      {
        label: 'PROSPECT',
        provider: MINI_SEARCH_V2_PROVIDERS.PROSPECT,
        total: prospects.total_items,
        items: [...prospects.items],
        fullyLoaded: false,
      },
      {
        label: 'BLOCKED',
        provider: MINI_SEARCH_V2_PROVIDERS.BLOCKED,
        total: blocked.total_items,
        items: [...blocked.items],
        fullyLoaded: false,
      },
    ];
    for (const seg of next) {
      if (seg.items.length >= seg.total) {
        seg.fullyLoaded = true;
      }
    }
    this.segments.set(next);
    const centreSum =
      prospects.total_items + active.total_items + exClients.total_items;
    this.thisCentreTotal.set(centreSum);
    this.allCentresTotal.set(sr.total_items ?? centreSum);
  }

  private searchCompany(
    query: string,
    reset: boolean,
    epoch: number,
  ): Promise<void> {
    this.loading.set(true);
    const page = reset ? 1 : this.companyPage();
    const request: OmniSearchRequestByCompany = {
      language: this.locale,
      query,
      tenant_ids: [],
      status: [],
      client_rep_ids: [],
      source: [],
      channel: [],
      show_only_web_leads: false,
      pagination: { page_number: page, page_size: 20 },
    };
    return new Promise((resolve) => {
      this.activeOmnisearchSub?.unsubscribe();
      this.activeOmnisearchSub = this.omniSearchService.searchByCompany(request).subscribe({
        next: (response: unknown) => {
          if (this.isOmnisearchStale(epoch)) {
            this.loading.set(false);
            resolve();
            return;
          }
          const r = response as {
            items?: Client[];
            total_items?: number;
            search_results?: { items?: Client[]; total_items?: number };
          };
          const items =
            r?.items ?? r?.search_results?.items ?? ([] as Client[]);
          const total = r?.total_items ?? r?.search_results?.total_items ?? 0;
          if (reset) {
            this.companyClients.set(items);
            this.companyPage.set(1);
          } else {
            this.companyClients.update((prev) => [...prev, ...items]);
          }
          this.companyHasMore.set(items.length >= 20);
          this.allCentresTotal.set(total || items.length);
          this.segments.set([]);
          this.loading.set(false);
          resolve();
        },
        error: () => {
          if (this.isOmnisearchStale(epoch)) {
            this.loading.set(false);
            resolve();
            return;
          }
          if (reset) {
            this.companyClients.set([]);
            this.companyPage.set(1);
          } else {
            this.companyPage.update((p) => Math.max(1, p - 1));
          }
          this.loading.set(false);
          resolve();
        },
      });
    });
  }

  private loadClientById(id: number): void {
    this.loadClientByIdSub?.unsubscribe();
    const request = {
      language: this.locale,
      query: String(id),
      providers: [
        {
          provider_name: ElasticProviderName.Clients,
          pagination: { page_number: 0, page_size: 10 },
        },
      ],
    };
    this.loadClientByIdSub = this.omniSearchService.Search(request).subscribe({
      next: (search: {
        searchResults?: { clients?: { items?: Client[] } };
        search_results?: { clients?: { items?: Client[] } };
      }) => {
        const items =
          search?.searchResults?.clients?.items ??
          search?.search_results?.clients?.items ??
          [];
        const client = items.find((o) => o.clt_id === id) ?? items[0];
        this.selectedReadonlyClient.set(client ?? null);
        if (client?.clt_id) {
          this.clients.emit(client);
        }
      },
      error: () => {
        this.selectedReadonlyClient.set(null);
      },
    });
  }
}
